import * as React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Settings, Task } from '../../types'
import ResourceCheckbox from '../Resource/ResourceCheckbox'
import TableContainer from './TableContainer'
import Table from './Table'
import TableCell from './TableCell'
import TableHead from './TableHead'
import TableCellContent from './TableCellContent'
import TableBody from './TableBody'
import TableRow from './TableRow'
import TaskTitle from './TaskTitle'
import moment from '../../utilities/Moment'
import TaskHelper from '../../helpers/TaskHelper'
import Icon from '../Icons/Icon'
import { useDebouncedCallback } from 'use-debounce'
import { Style } from '../../styles'
import TaskLabel from './TaskLabel'
import Avatar from '../Avatar/Avatar'
import AvatarStack from '../Avatar/AvatarStack'
import TaskIndicatorsContainer from './TaskIndicatorsContainer'
import TaskIndicators from './TaskIndicators'
import { TaskViewMode } from './TaskView'
import TableSortButton from './TableSortButton'
import getTaskTableRenderer from '../Resource/TaskRenderer'

const BoardIndicator = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	width: 4px;
	overflow: hidden;
	padding-left: 2px;
	position: absolute;
	left: -6px;
	border-radius: 3px;
	z-index: 999;
	cursor: pointer;

	&:hover {
		overflow: visible;
		background-color: transparent !important;
	}
`

const BoardIndicatorContent = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	position: relative;
	height: 100%;
	padding: 4px 8px;
	color: white;
	border-radius: 3px;
	margin-left: -2px;

	svg {
		width: 15px;
		margin-right: 4px;
	}
`

const Container = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	height: 100%;
	width: 100%;
`

const CheckboxContainer = styled.div`
	margin-right: 8px;
`

const TableCellTaskName = styled(TableCell)`
	width: 287.223px;
	min-width: 287.223px;
	max-width: 287.223px;

	@media screen and (max-width: ${Style.breakpoints.SMALL}) {
		width: 150px;
		min-width: 150px;
		max-width: 150px;
	}
`

const EstimatedTimeContainer = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	grid-gap: 4px;

`

type IProps = {
	setting: Settings
	mode?: TaskViewMode
	tasks: Task[]
	onTaskClick: (task: Task) => void
	onTaskChange: (task: Task) => void
	onTaskBoardClick: (task: Task) => void
	maxHeight?: string
	dateFormat: string
	sortValue?: string
	onSortChange?: (sortValue: string) => void
	onEndReached?: () => void
}

const TasksTable = (props: IProps) => {
	const container = React.useRef<HTMLDivElement>()
	const { t } = useTranslation()
	const { setting, mode, tasks, dateFormat, maxHeight, sortValue, onSortChange, onEndReached } = props

	const debouncedScrollChange = useDebouncedCallback(
		(event) => onScrollChange(event), 50
	);

	const onScrollChange = (event) => {
		var node = event.target;
		const endReached = node.scrollHeight - node.scrollTop - 200 <= node.clientHeight;

		if (endReached && onEndReached) onEndReached()
	}

	React.useEffect(() => {
		if (container.current) container.current.addEventListener('scroll', debouncedScrollChange)

		return () => {
			if (container.current) container.current.removeEventListener('scroll', debouncedScrollChange)
		}
	}, [])

	const onTaskClick = (task: Task) => {
		props.onTaskClick(task)
	}

	const onTaskCompletedChange = (task: Task) => {
		props.onTaskChange({ ...task, completed: !task.completed })
	}

	const onSortToggle = (sortValue) => {
		props.onSortChange(sortValue)
	}

	const { renderRow, renderHeader } = getTaskTableRenderer({
		setting: setting,
		sortValue: sortValue,
		onRowClick: onTaskClick,
		onSortChange: onSortToggle,
		onCompletedChange: onTaskCompletedChange,
	})

	return (
		<Container>
			<TableContainer ref={container} maxHeight={maxHeight}>
				<Table>
					<TableHead>
						{renderHeader()}
					</TableHead>

					<TableBody>
						{tasks.map((task, index) => {
							return renderRow(task, index)
						})}
					</TableBody>
				</Table>
			</TableContainer>
		</Container>
	)
}

export default TasksTable